import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { safeAuthFetch } from '../../utils/safeFetch';
import { API_BASE_URL } from '../../config/api';
import { posthog } from '../../main';
import { SCENARIO_CONTEXTS, SCENARIO_CONTEXT_LABELS, GUEST_EXCLUDED_CONTEXTS } from '../../constants/scenarioContexts';
import { fetchDiscoveryShortlist } from '../../lib/discovery/discoveryApi';
import { startGuestRun } from '../../lib/discovery/startGuestRun';
import { guestStartErrorMessage } from '../../lib/discovery/guestStartErrorMessage';
import { useDiscoveryOrigin } from '../../lib/discovery/discoveryOrigin';
import { contextPresentation } from '../../components/guestDiscovery/contextPresentation';
import ScenarioCard from '../../components/guestDiscovery/ScenarioCard';
import CommitmentSheetOverlay from '../../components/guestDiscovery/CommitmentSheetOverlay';
import { useSystemThemeWhenUnset } from '../../lib/theme/useSystemThemeWhenUnset';
import './guestDiscoveryShared.css';
import './GuestDiscoveryShortlistPage.css';

/**
 * GuestDiscoveryShortlistPage — J3 shortlist (#1921, copy round 2 #2098
 * call 4).
 *
 * Route: `/discover/:contextSlug`. Renders ALL curated scenarios the API
 * returns for the context — the route kept its "shortlist" name (deployed
 * clients call it) but is no longer top-3-only (#2098 call 4, decided
 * 2026-08-12: the chip count on J2 was lying about a top-3-only list). This
 * page does not re-sort — the API already ordered the full set by the
 * #1903 v0 metric with the editorial sort_priority override on top. Fix
 * round 2026-08-24 (Gilbert, ephemeral human-test): the original "ranked
 * head of 3 + quiet divider + rest" split is DROPPED in favour of one
 * continuous list — ordering itself is unchanged, only the visual break is
 * removed. Selecting a card lands J4 (`CommitmentSheetOverlay`) over this
 * shortlist, dimmed — the overlay itself owns the scrim.
 */
function GuestDiscoveryShortlistPage() {
  useSystemThemeWhenUnset(); // #3756: match the marketing site's OS dark mode
  const { contextSlug } = useParams();
  useDiscoveryOrigin(); // records ?from= for /discover's back link
  const navigate = useNavigate();
  const { isAuthenticated, user, loginAsGuest } = useAuth();

  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [committingScenario, setCommittingScenario] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState(null);

  const isKnownContext = SCENARIO_CONTEXTS.includes(contextSlug) && !GUEST_EXCLUDED_CONTEXTS.includes(contextSlug);
  const label = SCENARIO_CONTEXT_LABELS[contextSlug];
  const { icon } = contextPresentation(contextSlug);

  useEffect(() => {
    if (!isKnownContext) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);
    fetchDiscoveryShortlist(contextSlug)
      .then((scenarios) => { if (!cancelled) setCards(scenarios); })
      .catch(() => { if (!cancelled) setLoadError(true); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [contextSlug, isKnownContext]);

  // Scope C: fire once per distinct shortlist render (context + the actual
  // ordered set of ids), not on every re-render this effect happens to run.
  const impressionKeyRef = useRef(null);
  useEffect(() => {
    if (isLoading || loadError || cards.length === 0) return;
    const key = `${contextSlug}:${cards.map((c) => c.id).join(',')}`;
    if (impressionKeyRef.current === key) return;
    impressionKeyRef.current = key;
    posthog?.capture?.('discovery_shortlist_impression', {
      context: contextSlug,
      scenario_ids: cards.map((c) => c.id),
    });
  }, [isLoading, loadError, cards, contextSlug]);

  const handleSelectCard = useCallback((card, position) => {
    posthog?.capture?.('discovery_card_tapped', {
      scenario_id: card.id,
      prep_required: !!card.prep_required,
      position,
    });
    setCommittingScenario(card);
  }, []);

  const handleStart = useCallback(async (modality) => {
    if (!committingScenario) return;
    setIsStarting(true);
    setStartError(null);
    posthog?.capture?.('discovery_run_started', {
      scenario_id: committingScenario.id,
      modality,
    });
    try {
      await startGuestRun({
        scenarioId: committingScenario.id,
        modality,
        isAuthenticated,
        user,
        loginAsGuest,
        safeAuthFetch,
        apiBaseUrl: API_BASE_URL,
        navigate,
      });
    } catch (err) {
      console.error('Failed to start guest session:', err);
      setStartError(guestStartErrorMessage(err, 'Something went wrong starting that conversation. Try again.'));
      setIsStarting(false);
    }
  }, [committingScenario, isAuthenticated, user, loginAsGuest, navigate]);

  if (!isKnownContext) {
    return (
      <div className="gd-page gd-shortlist-page">
        <header className="gd-page__header">
          <Link to="/discover" className="gd-page__back">← Contexts</Link>
        </header>
        <p className="gd-page__status gd-page__status--error">
          We don't recognise that scenario context. <Link to="/discover">Back to contexts</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="gd-page gd-shortlist-page">
      <header className="gd-page__header">
        <Link to="/discover" className="gd-page__back">← Contexts</Link>
      </header>

      <h1 className="gd-page__title">
        <span aria-hidden="true">{icon}</span> {label}
      </h1>

      {isLoading && <p className="gd-page__status">Loading scenarios…</p>}
      {!isLoading && loadError && (
        <p className="gd-page__status gd-page__status--error">
          Couldn't load this shortlist right now. <Link to="/discover">Back to contexts</Link>
        </p>
      )}
      {!isLoading && !loadError && cards.length === 0 && (
        <p className="gd-page__status">
          No scenarios ready in this context yet. <Link to="/discover">Try another context</Link>
        </p>
      )}

      {/* #2098-round-2 (Gilbert, 2026-08-24): the "More in {label} — N
          others" divider is DROPPED — one continuous list. Ordering is
          unchanged (still whatever the API returned, #2098 call 4's full
          eligible set, un-re-sorted here). */}
      {!isLoading && !loadError && cards.length > 0 && (
        <div className="gd-shortlist-page__cards">
          {cards.map((card, index) => (
            <ScenarioCard key={card.id} card={card} onSelect={() => handleSelectCard(card, index)} />
          ))}
        </div>
      )}

      {startError && <p className="gd-page__status gd-page__status--error">{startError}</p>}

      <CommitmentSheetOverlay
        scenario={committingScenario}
        isStarting={isStarting}
        onDismiss={() => { setCommittingScenario(null); setStartError(null); }}
        onStart={handleStart}
      />
    </div>
  );
}

export default GuestDiscoveryShortlistPage;
